import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { LARAVEL_BASE, getAuthHeaders } from '@/api/client'
import type { User } from '@/types'

// Auth endpoints live on Laravel (/api/v2), not Express (/api)
async function laravelRequest<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${LARAVEL_BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options?.headers,
    },
    ...options,
  })

  if (res.status === 401) {
    if (window.location.pathname !== '/login') {
      window.location.href = '/login'
    }
    throw new Error('Not authenticated')
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${res.status}`)
  }

  if (res.status === 204) return undefined as T
  return res.json()
}

export function useAuth() {
  return useQuery<User>({
    queryKey: ['auth', 'me'],
    queryFn: () => laravelRequest('/auth/me'),
    retry: false,
    staleTime: 5 * 60 * 1000,
  })
}

async function getDeviceFingerprint(): Promise<string> {
  const raw = [
    navigator.userAgent,
    navigator.language,
    navigator.languages?.join(','),
    screen.width + 'x' + screen.height,
    screen.colorDepth,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.hardwareConcurrency,
    (navigator as any).deviceMemory,
    navigator.platform,
    new Date().getTimezoneOffset(),
  ].join('|')

  const encoder = new TextEncoder()
  const data = encoder.encode(raw)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

function getDeviceName(): string {
  const ua = navigator.userAgent
  if (/iPhone/.test(ua)) return 'iPhone'
  if (/iPad/.test(ua)) return 'iPad'
  if (/Android/.test(ua)) return 'Android'
  if (/Mac/.test(ua)) return 'Mac'
  if (/Windows/.test(ua)) return 'Windows'
  if (/Linux/.test(ua)) return 'Linux'
  return 'Unknown'
}

export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: { username: string; password: string }) => {
      const fingerprint = await getDeviceFingerprint()
      const device_name = getDeviceName()
      return laravelRequest<{ user: User; token?: string }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ ...data, fingerprint, device_name }),
      })
    },
    onSuccess: (data) => {
      if (data.token) localStorage.setItem('auth_token', data.token)
      queryClient.setQueryData(['auth', 'me'], data.user)
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => laravelRequest('/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      localStorage.removeItem('auth_token')
      queryClient.clear()
      window.location.href = '/login'
    },
  })
}

export function useSaveTheme() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (theme: string) =>
      laravelRequest('/auth/theme', {
        method: 'PUT',
        body: JSON.stringify({ theme }),
      }),
    onSuccess: (_, theme) => {
      queryClient.setQueryData<User>(['auth', 'me'], (old) =>
        old ? { ...old, theme: theme as 'dark' | 'light' } : old,
      )
    },
  })
}
